/**
 * Haphe theme registry.
 *
 * Two flavors of theme co-exist:
 *
 * - **Classic** presets (bone, ink, moss, sand, slate) — original 5 color
 *   palettes. They render through the standard photo-on-left hero in
 *   PublicCardLayout. Kept indefinitely for backward compat with cards
 *   created before frames existed.
 *
 * - **Frames** (mono, cream, grid, editorial, sticker, ticket, blueprint,
 *   neon) — the 8 visual frame variants marketed on the landing page. Each
 *   one has its own structural hero layout in CardHero.tsx alongside its
 *   color palette here. Picking a frame is picking a complete card design,
 *   not just colors.
 *
 * - **Custom** — user-defined palette. Renders through the classic hero
 *   using the four custom_* color fields on the card row.
 *
 * The DB CHECK constraint in migration 0009 enforces this exact set of
 * keys.
 */

export type ClassicKey = 'bone' | 'ink' | 'moss' | 'sand' | 'slate';
export type FrameKey =
  | 'mono'
  | 'cream'
  | 'grid'
  | 'editorial'
  | 'sticker'
  | 'ticket'
  | 'blueprint'
  | 'neon';
export type ThemeKey = ClassicKey | FrameKey | 'custom';

export interface ThemePreset {
  /** Page background — visible around / behind the contact card. */
  pageBg: string;
  /** Contact card surface color. */
  cardBg: string;
  /** Primary text + outline button border + filled CTA fill. */
  text: string;
  /** Icons, hover states, secondary highlights. */
  accent: string;
}

/** The original 5 color presets — unchanged. */
export const CLASSIC_THEMES: Record<ClassicKey, ThemePreset> = {
  bone:  { pageBg: '#F2EFE8', cardBg: '#FAF8F4', text: '#0A0A0A', accent: '#3A4A3F' },
  ink:   { pageBg: '#000000', cardBg: '#171717', text: '#FAF8F4', accent: '#A8B5A8' },
  moss:  { pageBg: '#2A3530', cardBg: '#3A4A3F', text: '#FAF8F4', accent: '#A8B5A8' },
  sand:  { pageBg: '#D6CAA9', cardBg: '#E8DFC9', text: '#2A2418', accent: '#7A6543' },
  slate: { pageBg: '#15212A', cardBg: '#1F2A30', text: '#E8E4DB', accent: '#A8B5A8' },
};

/**
 * The 8 new frames. Colors mirror the `.lp-gframe--*` rules in
 * the marketing site's hero so the actual card matches the marketing card.
 *
 * The shared warm-gold accent (oklch(0.78 0.12 75)) only renders correctly
 * in browsers that support oklch in solid color contexts (basically every
 * evergreen browser as of 2024). We use it as the accent for every frame
 * — the brand on the landing carries through to the product.
 */
const ACCENT_WARM = 'oklch(0.78 0.12 75)';

export const FRAME_THEMES: Record<FrameKey, ThemePreset> = {
  mono:      { pageBg: '#050403', cardBg: '#0A0908', text: '#F5F2EC', accent: ACCENT_WARM },
  cream:     { pageBg: '#EFEBE2', cardBg: '#F5F2EC', text: '#0A0908', accent: ACCENT_WARM },
  grid:      { pageBg: '#050403', cardBg: '#0A0908', text: '#F5F2EC', accent: ACCENT_WARM },
  editorial: { pageBg: '#EFEBE2', cardBg: '#F5F2EC', text: '#0A0908', accent: ACCENT_WARM },
  sticker:   { pageBg: '#0A0908', cardBg: '#14110E', text: '#F5F2EC', accent: ACCENT_WARM },
  ticket:    { pageBg: '#EFEBE2', cardBg: '#F5F2EC', text: '#0A0908', accent: ACCENT_WARM },
  blueprint: { pageBg: '#152033', cardBg: '#1E2E45', text: '#DDEBFF', accent: '#7FB3FF' },
  neon:      { pageBg: '#050403', cardBg: '#0A0908', text: '#F5F2EC', accent: ACCENT_WARM },
};

/** Combined map — used for `key in CARD_THEMES` checks. */
export const CARD_THEMES: Record<ClassicKey | FrameKey, ThemePreset> = {
  ...CLASSIC_THEMES,
  ...FRAME_THEMES,
};

/** Picker grouping. Order matters — this is the display order in the editor. */
export const FRAME_KEYS: FrameKey[] = [
  'mono',
  'cream',
  'grid',
  'editorial',
  'sticker',
  'ticket',
  'blueprint',
  'neon',
];

export const CLASSIC_KEYS: ClassicKey[] = ['bone', 'ink', 'moss', 'sand', 'slate'];

/**
 * Legacy export — every concrete key the picker may show.
 * Order: frames first (the new marketing-forward defaults), then classic
 * color presets, then 'custom' last so the bespoke palette stays the
 * "advanced" choice.
 */
export const THEME_KEYS: ThemeKey[] = [
  ...FRAME_KEYS,
  ...CLASSIC_KEYS,
  'custom',
];

/**
 * Frame display metadata — used by the editor picker and the landing
 * marketing copy (src/marketing).
 */
export const FRAME_META: Record<FrameKey, { name: string; mood: string; use: string }> = {
  mono:      { name: 'Monolith',  mood: 'Quiet, type-driven, technical.',  use: 'Founders, engineers, anyone tired of color.' },
  cream:     { name: 'Cream',     mood: 'Warm, editorial, hand-feel.',     use: 'Designers, agencies, writers.' },
  grid:      { name: 'Grid',      mood: 'Schematic, monospace, exact.',    use: 'Devs, researchers, infra.' },
  editorial: { name: 'Editorial', mood: 'Serif-led, considered, slow.',    use: 'Investors, journalists, advisors.' },
  sticker:   { name: 'Sticker',   mood: 'Loud, friendly, party.',          use: 'Founders at conferences, creators.' },
  ticket:    { name: 'Ticket',    mood: 'Event-stub, occasion-bound.',     use: 'Speakers, performers, hosts.' },
  blueprint: { name: 'Blueprint', mood: 'Drafted, technical, blue-paper.', use: 'Architects, engineers, makers.' },
  neon:      { name: 'Neon',      mood: 'Late-night, signal-on, vivid.',   use: 'DJs, nightlife, bands.' },
};

/** Defaults seeded when a user first switches to 'custom'. Same as 'bone'. */
export const CUSTOM_DEFAULTS: ThemePreset = { ...CLASSIC_THEMES.bone };

export interface CustomColors {
  custom_bg?: string | null;
  custom_card_bg?: string | null;
  custom_text?: string | null;
  custom_accent?: string | null;
}

/** Resolves the runtime theme — overlays custom_* fields when `key === 'custom'`. */
export function getTheme(key: string | null | undefined, custom?: CustomColors): ThemePreset {
  if (key === 'custom') {
    return {
      pageBg: custom?.custom_bg || CUSTOM_DEFAULTS.pageBg,
      cardBg: custom?.custom_card_bg || CUSTOM_DEFAULTS.cardBg,
      text: custom?.custom_text || CUSTOM_DEFAULTS.text,
      accent: custom?.custom_accent || CUSTOM_DEFAULTS.accent,
    };
  }
  if (key && key in CARD_THEMES) {
    return CARD_THEMES[key as ClassicKey | FrameKey];
  }
  return CLASSIC_THEMES.bone;
}

/** Used by editor swatch grid (no DB row required). */
export function previewTheme(key: ThemeKey, custom?: CustomColors): ThemePreset {
  if (key === 'custom') return getTheme('custom', custom);
  return CARD_THEMES[key];
}

/** `true` when the key is one of the 8 structural frames. */
export function isFrameKey(key: string | null | undefined): key is FrameKey {
  return !!key && key in FRAME_THEMES;
}

/**
 * WCAG 2.x contrast ratio between two hex colors (1–21). Returns null when
 * either value isn't parseable hex (e.g. oklch frames) so callers can skip
 * the check rather than warn on a false reading.
 */
export function contrastRatio(hexA: string, hexB: string): number | null {
  const a = relativeLuminance(hexA);
  const b = relativeLuminance(hexB);
  if (a == null || b == null) return null;
  const [hi, lo] = a >= b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

function relativeLuminance(hex: string): number | null {
  const h = hex.replace('#', '');
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(h)) return null;
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const channel = (i: number) => {
    const v = parseInt(full.slice(i, i + 2), 16) / 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

export function isLightColor(hex: string): boolean {
  // Quick perceived-brightness test.
  // Handles #RGB, #RRGGBB, and gives any non-hex value (e.g. oklch(...))
  // a "dark" default so contrast logic stays conservative.
  const h = hex.replace('#', '');
  if (!/^[0-9a-fA-F]{3,8}$/.test(h)) return false;
  let r = 0, g = 0, b = 0;
  if (h.length === 3) {
    r = parseInt(h[0] + h[0], 16);
    g = parseInt(h[1] + h[1], 16);
    b = parseInt(h[2] + h[2], 16);
  } else if (h.length >= 6) {
    r = parseInt(h.slice(0, 2), 16);
    g = parseInt(h.slice(2, 4), 16);
    b = parseInt(h.slice(4, 6), 16);
  }
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55;
}
